/** What can be applied for, which is exactly what has been published. */
import { api, ApiError } from "../../lib/api.ts";
import { useRouter } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import { Empty, Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";
import { useState } from "react";
import type { CatalogueEntry } from "./types.ts";

export function Catalogue() {
  const { can } = useAuth();
  const { navigate } = useRouter();
  const catalogue = useAsync(() => api.get<{ products: CatalogueEntry[] }>("/api/catalogue"), []);
  const [problem, setProblem] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);

  const products = catalogue.data?.products ?? [];

  async function start(code: string) {
    setProblem(null);
    setStarting(code);
    try {
      const created = await api.post<{ application: { id: string } }>("/api/applications", { productCode: code });
      navigate(`/applications/${created.application.id}`);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not start an application.");
      setStarting(null);
    }
  }

  return (
    <div className="stack">
      <PageHead title="Product catalogue" sub="Every published product, and what it asks for." />

      <Tiles
        tiles={[
          { label: "Products available", value: products.length, tone: "accent" },
          { label: "Lines of business", value: new Set(products.map((product) => product.lineOfBusiness)).size, tone: "info" },
          { label: "Questions across all", value: products.reduce((total, product) => total + product.fieldCount, 0), tone: "ok" },
          { label: "Sections", value: products.reduce((total, product) => total + product.sectionCount, 0), tone: "muted" },
        ]}
      />

      {problem && <div className="notice notice-bad">{problem}</div>}
      <Problem error={catalogue.error} />

      {catalogue.loading && !catalogue.data ? (
        <Loading what="the catalogue" />
      ) : products.length === 0 ? (
        <Empty title="Nothing published yet">
          A product appears here the moment it is published in the configuration studio.
        </Empty>
      ) : (
        <div className="catalogue">
          {products.map((product) => (
            <div className="card product-card" key={product.code}>
              <div className="card-body stack-sm">
                <div className="row-between">
                  <h2>{product.name}</h2>
                  <span className="chip chip-ok">v{product.version}</span>
                </div>
                <p className="small muted">{product.lineOfBusiness}</p>
                {product.description && <p className="small">{product.description}</p>}
                <div className="chips">
                  <span className="chip">{product.sectionCount} sections</span>
                  <span className="chip">{product.fieldCount} questions</span>
                </div>
              </div>
              {can("applications", "edit") && (
                <div className="card-foot">
                  <button
                    className="btn btn-primary" type="button" disabled={starting === product.code}
                    onClick={() => void start(product.code)}
                  >
                    {starting === product.code ? "Starting…" : "Start an application"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
